// Read-only popover that lists a deliverable's evidence links.
// Anchored to the row's EvidenceIndicator and rendered via a React
// portal to document.body so the table's overflow:auto wrapper can't
// clip it. Closes on Escape and outside-click.
//
// Positioning uses `position: fixed` so the viewport-relative
// coordinates from `getBoundingClientRect()` apply directly without
// composing with `window.scrollY`. With `absolute`, scrolling the
// page would drift the popover away from its anchor.

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ExternalLink } from "lucide-react";
import type { EvidenceLink } from "../../../../types";
import { safeExternalHref } from "../../../../utils/evidenceLinks";

export interface EvidenceLinkPopoverProps {
  rowId: string;
  links: EvidenceLink[];
  /** Bounding rect of the trigger button so we can position the popover. */
  anchorRect: DOMRect;
  onClose: () => void;
}

export default function EvidenceLinkPopover({
  rowId,
  links,
  anchorRect,
  onClose,
}: EvidenceLinkPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const handleClick = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKey);
    // Defer the click-listener install by a tick so the click that
    // opened the popover doesn't immediately close it.
    const handle = setTimeout(() => {
      document.addEventListener("mousedown", handleClick);
    }, 0);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
      clearTimeout(handle);
    };
  }, [onClose]);

  // Position below the trigger by default; flip-to-top if there's no
  // room. window.innerHeight check keeps the popover inside the viewport
  // for triggers near the bottom of the table.
  const POPOVER_HEIGHT_ESTIMATE = 200;
  const flipAbove =
    anchorRect.bottom + POPOVER_HEIGHT_ESTIMATE > window.innerHeight;
  const top = flipAbove
    ? Math.max(8, anchorRect.top - POPOVER_HEIGHT_ESTIMATE - 4)
    : anchorRect.bottom + 4;

  return createPortal(
    <div
      ref={popoverRef}
      data-testid={`evidence-popover-${rowId}`}
      role="dialog"
      aria-label="Evidence attachments"
      style={{
        position: "fixed",
        top,
        left: anchorRect.left,
        zIndex: 50,
      }}
      className="min-w-[220px] max-w-[360px] max-h-[60vh] overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800"
    >
      <ul className="space-y-1">
        {links.map((link, i) => {
          const href = safeExternalHref(link.url);
          const text = link.label || link.url;
          return (
            <li key={`${rowId}-evidence-${i}`}>
              {href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-body-sm text-brand-600 hover:underline dark:text-brand-400"
                >
                  {text}
                  <ExternalLink aria-hidden="true" className="h-3 w-3" />
                </a>
              ) : (
                <span className="inline-flex items-center gap-1 text-body-sm text-slate-700 dark:text-slate-300">
                  {text}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>,
    document.body,
  );
}
